/**
 * COMPARATEUR — les deux modes et la modale d'ajout.
 *
 * Le tableau des gammes est servi en HTML : c'est le contenu que les
 * moteurs lisent, et il vit sans ce fichier. Ce script n'ajoute que le
 * second mode — celui où l'on ajoute, retire et réorganise des colonnes,
 * ce qui ne se fait pas sans script. Il commence donc par révéler la
 * bascule, cachée jusque-là pour ne pas mener à un mode mort.
 *
 * Les produits comparables et leurs valeurs viennent du Liquid, en JSON :
 * le client change un critère dans l'éditeur, le tableau suit.
 */

const racine = document.querySelector('[data-cmp-donnees]')?.closest('.shopify-section') ?? document;
const source = racine.querySelector('[data-cmp-donnees]');

if (source) {
  let data;
  try {
    data = JSON.parse(source.textContent);
  } catch {
    /* Données illisibles : on laisse la page sur son tableau de gammes
       plutôt que d'ouvrir un mode qui ne saurait rien afficher. */
    data = null;
  }

  if (data?.produits?.length) {
    const T = data.textes;
    const CRIT = data.criteres ?? [];
    const PAR_CLE = new Map(data.produits.map((p) => [p.cle, p]));

    const modes = racine.querySelector('[data-cmp-modes]');
    const vues = racine.querySelectorAll('[data-cmp-vue]');
    const barre = racine.querySelector('[data-cmp-barre]');
    const sortie = racine.querySelector('[data-cmp-sortie]');
    const modale = racine.querySelector('[data-cmp-modale]');
    const recherche = racine.querySelector('[data-cmp-recherche]');
    const liste = racine.querySelector('[data-cmp-liste]');
    const titre = racine.querySelector('[data-cmp-titre]');

    /* Le départ ne garde que ce que le catalogue contient vraiment : un
       produit dépublié ne doit pas laisser une colonne vide. */
    let choisis = (data.depart ?? []).filter((k) => PAR_CLE.has(k));

    const echapper = (s) =>
      String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    /* La recherche ignore les accents : « dubai » doit trouver « Dubaï ». */
    const plat = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

    const remplir = (modele, quoi, par) => String(modele ?? '').replaceAll(quoi, par);

    /* ── la bascule des modes ── */
    if (modes) modes.hidden = false;

    for (const onglet of racine.querySelectorAll('[data-cmp-mode]')) {
      onglet.addEventListener('click', () => {
        const vise = onglet.dataset.cmpMode;
        for (const autre of racine.querySelectorAll('[data-cmp-mode]')) {
          const actif = autre === onglet;
          autre.classList.toggle('is-on', actif);
          autre.setAttribute('aria-selected', String(actif));
        }
        for (const vue of vues) vue.hidden = vue.dataset.cmpVue !== vise;
      });
    }

    /* ── le rappel de la sélection ── */
    function peindreBarre() {
      barre.innerHTML =
        /* mobile : un seul déclencheur façon « Filtres » de collection — la
           sélection se gère DANS la feuille (retour mhkdesign 2026-09-13).
           Les puces restent la version desktop. */
        `<button class="cf-trig cmpp-bar-trig" type="button" data-cmp-ouvrir="selection">` +
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="M4 7h16M7 12h10M10 17h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
        `${echapper(remplir(T.declencheur, '[n]', choisis.length))}</button>` +
        `<span class="cmpp-bar-l">${echapper(T.selection)}</span>` +
        choisis
          .map((k) => {
            const p = PAR_CLE.get(k);
            return (
              `<span class="cmpp-chip">${echapper(p.nom)}` +
              `<button type="button" data-cmp-retirer="${echapper(k)}" ` +
              `aria-label="${echapper(`${T.retirer} ${p.nom}`)}">×</button></span>`
            );
          })
          .join('') +
        `<button class="cmpp-bar-add" type="button" data-cmp-ouvrir>${echapper(T.ajouter)}</button>`;
    }

    /* ── une cellule, selon la nature du critère ── */
    function cellule(p, i) {
      const c = CRIT[i];
      const v = p.valeurs?.[i];
      if (c.type === 'prix') {
        return (
          `<b class="cmp-price">${echapper(T.des)} ${echapper(p.prix)}</b>` +
          (p.prixBarre ? ` <s class="cmpp-old">${echapper(p.prixBarre)}</s>` : '')
        );
      }
      if (c.type === 'oui-non') {
        /* Un tiret plutôt qu'un « Non » : l'absence se voit sans peser. */
        return v === true || v === 'true' || v === T.oui
          ? `<span class="cmpp-oui">${echapper(T.oui)}</span>`
          : `<span class="cmpp-non" aria-label="${echapper(T.nonLu)}">${echapper(T.non)}</span>`;
      }
      return v ? echapper(v) : echapper(T.non);
    }

    /* ── le tableau des modèles ── */
    function peindreTable() {
      if (!choisis.length) {
        sortie.innerHTML =
          '<div class="cmpp-empty">' +
          `<p>${echapper(T.vide)}</p>` +
          `<button class="gm-btn gm-btn--cta" type="button" data-cmp-ouvrir>${echapper(T.videCta)}</button>` +
          '</div>';
        return;
      }

      const n = choisis.length;
      const compte = remplir(n === 1 ? T.compteUn : T.compte, '[n]', n);

      const entetes = choisis
        .map((k, i) => {
          const p = PAR_CLE.get(k);
          const vignette = p.img
            ? `<img class="cmpp-img" src="${echapper(p.img)}" alt="${echapper(p.alt)}" ` +
              'width="320" height="240" loading="lazy" decoding="async">'
            : '';
          return (
            '<th scope="col">' +
            '<span class="cmpp-tools">' +
            `<button type="button" data-cmp-bouge="${echapper(k)}|-1"${i === 0 ? ' disabled' : ''} ` +
            `aria-label="${echapper(remplir(T.gauche, '[produit]', p.nom))}">◀</button>` +
            `<button type="button" class="cmpp-rm" data-cmp-retirer="${echapper(k)}" ` +
            `aria-label="${echapper(`${T.retirer} ${p.nom}`)}">×</button>` +
            `<button type="button" data-cmp-bouge="${echapper(k)}|1"${i === n - 1 ? ' disabled' : ''} ` +
            `aria-label="${echapper(remplir(T.droite, '[produit]', p.nom))}">▶</button>` +
            '</span>' +
            vignette +
            `<span class="cmpp-name">${echapper(p.nom)}</span>` +
            `<span class="cmpp-tag">${echapper(p.type)}</span>` +
            '</th>'
          );
        })
        .join('');

      /* Un critère que pas un seul modèle affiché ne renseigne ne dit
         rien : la rangée de tirets est retirée. Elle revient d'elle-même
         le jour où le métachamp est rempli. Le prix, lui, vient du
         produit et ne peut pas manquer. */
      const utile = (i) =>
        CRIT[i].type === 'prix' ||
        choisis.some((k) => {
          const v = PAR_CLE.get(k).valeurs?.[i];
          return v !== null && v !== undefined && v !== '' && v !== false;
        });

      const corps = CRIT.map((c, i) =>
        utile(i)
          ? `<tr><th scope="row">${echapper(c.label)}</th>` +
            choisis.map((k) => `<td>${cellule(PAR_CLE.get(k), i)}</td>`).join('') +
            '</tr>'
          : '',
      ).join('');

      sortie.innerHTML =
        '<div class="pd-cmp-wrap"><table class="pd-cmp cmpp-table cmpp-mod">' +
        `<caption class="sr-only">${echapper(T.legende)}</caption>` +
        `<thead><tr><th scope="col"><span class="cmpp-count">${echapper(compte)}</span></th>` +
        entetes +
        '</tr></thead>' +
        `<tbody>${corps}</tbody></table></div>`;
    }

    /* ── une ligne de la modale : la même dans les deux vues ── */
    function ligne(p) {
      const pris = choisis.includes(p.cle);
      /* alt vide : le nom est juste à côté, le répéter alourdirait la
         lecture au lecteur d'écran. */
      const vignette = p.img
        ? `<img class="cmpp-pick-img" src="${echapper(p.img)}" alt="" width="56" height="42" ` +
          'loading="lazy" decoding="async">'
        : '';
      return (
        `<li><button type="button" class="cmpp-pick-it${pris ? ' is-sel' : ''}" ` +
        `data-cmp-bascule="${echapper(p.cle)}" aria-pressed="${pris}">` +
        vignette +
        `<span class="cmpp-pick-n">${echapper(p.nom)}</span>` +
        `<span class="cmpp-pick-p">${echapper(T.des)} ${echapper(p.prix)}</span>` +
        `<span class="cmpp-pick-a">${echapper(pris ? T.dejaChoisi : T.aChoisir)}</span>` +
        '</button></li>'
      );
    }

    /* ── la liste de la modale, selon la vue ── */
    function peindreListe() {
      if (modale.dataset.vue === 'selection') {
        const pris = choisis.map((k) => PAR_CLE.get(k)).filter(Boolean);
        liste.innerHTML = pris.length
          ? `<ul class="cmpp-pick">${pris.map(ligne).join('')}</ul>`
          : `<p class="cmpp-vide">${echapper(T.vide)}</p>`;
        return;
      }

      const q = plat(recherche.value.trim());
      const trouves = data.produits.filter(
        (p) => !q || plat(p.nom).includes(q) || plat(p.type).includes(q),
      );

      if (!trouves.length) {
        liste.innerHTML = `<p class="cmpp-vide">${echapper(
          remplir(T.sansResultat, '[recherche]', recherche.value),
        )}</p>`;
        return;
      }

      liste.innerHTML = `<ul class="cmpp-pick">${trouves.map(ligne).join('')}</ul>`;
    }

    /* ── ouvrir la modale sur l'une ou l'autre vue ── */
    function ouvrir(vue) {
      modale.dataset.vue = vue;
      if (titre) titre.textContent = vue === 'selection' ? titre.dataset.titreSelection : titre.dataset.titreAjout;
      recherche.value = '';
      peindreListe();
      if (!modale.open) modale.showModal();
      modale.scrollTop = 0;
      liste.scrollTop = 0;
      if (vue === 'ajout') recherche.focus();
    }

    const peindre = () => {
      peindreBarre();
      peindreTable();
      if (modale.open) peindreListe();
    };

    /* ── les gestes, délégués : le markup est refait à chaque peinture ── */
    racine.addEventListener('click', (evenement) => {
      const ouvre = evenement.target.closest('[data-cmp-ouvrir]');
      if (ouvre) {
        ouvrir(ouvre.dataset.cmpOuvrir === 'selection' ? 'selection' : 'ajout');
        return;
      }
      if (evenement.target.closest('[data-cmp-fermer]')) {
        modale.close();
        return;
      }
      /* Un clic sur le voile ferme la modale. Le voile n'est pas un élément :
         un clic hors de la boîte remonte sur le <dialog> lui-même, et on
         vérifie qu'il est tombé en dehors de son rectangle — un clic dans
         le padding de la boîte ne doit pas la fermer. */
      if (evenement.target === modale) {
        const boite = modale.getBoundingClientRect();
        const dedans =
          evenement.clientX >= boite.left &&
          evenement.clientX <= boite.right &&
          evenement.clientY >= boite.top &&
          evenement.clientY <= boite.bottom;
        if (!dedans) modale.close();
        return;
      }
      const retire = evenement.target.closest('[data-cmp-retirer]');
      if (retire) {
        choisis = choisis.filter((k) => k !== retire.dataset.cmpRetirer);
        peindre();
        return;
      }
      const bascule = evenement.target.closest('[data-cmp-bascule]');
      if (bascule) {
        const k = bascule.dataset.cmpBascule;
        choisis = choisis.includes(k) ? choisis.filter((x) => x !== k) : [...choisis, k];
        peindre();
        return;
      }
      const bouge = evenement.target.closest('[data-cmp-bouge]');
      if (bouge) {
        const [k, sens] = bouge.dataset.cmpBouge.split('|');
        const i = choisis.indexOf(k);
        const j = i + Number(sens);
        if (i >= 0 && j >= 0 && j < choisis.length) {
          [choisis[i], choisis[j]] = [choisis[j], choisis[i]];
          peindre();
        }
      }
    });

    recherche.addEventListener('input', peindreListe);

    peindre();
  }
}
